// Story 9.4 (ADO-1948): the shared submitted-at formatter for apps/web.
//
// The wire value is UTC-normalised ISO 8601 (the repo Dates rule, which governs what
// the API SENDS). What a shopkeeper READS is a different question, and the designs
// answer it: the frame draws a bare `DD/MM/YYYY`, so that is what ships, in the
// viewer's own zone and with no zone label. A store manager checking which order went
// in on Tuesday evening thinks in the clock on the wall, not in UTC.
//
// This was deliberately the other way round until 2026-09-18, rendering
// `YYYY-MM-DD HH:mm UTC` on the argument that the repo's UTC convention outranked the
// mock. The PO overruled that: the designs are the source of truth for this story, and
// AC1(b)'s "rendered UTC-normalised ISO 8601" is superseded for the DISPLAY value the
// same way AC4 was rewritten against the frames. The wire contract is unchanged.
//
// The TIME was shown alongside the date until 2026-09-22, on the reading that AC1 asks
// for "the submission date and time" and that two orders sent on one day are otherwise
// indistinguishable. It is now DROPPED from the display: the delivered Figma order card
// draws a bare date, and the card is a summary, not a forensic record. Nothing is lost
// to machines or to support — the full UTC instant stays on the `<time dateTime>`
// attribute at the call site, which is the half of the element meant to be unambiguous.
//
// `new Date(iso)` here parses an EXPLICIT string. It is not the bare `new Date()` the
// repo forbids (an implicit "now" that makes a render untestable).

/**
 * Render an ISO 8601 timestamp as `DD/MM/YYYY` in the viewer's local zone.
 *
 * LOCAL, not UTC, and that is load-bearing rather than cosmetic: the order-history date
 * FILTER (Story 9.6) resolves the day the user picked against this same local zone, so
 * the day a row shows and the day the filter selects are the same day. They were not
 * when the filter assumed UTC, and orders visibly dated the 21st were excluded by a
 * "from the 21st" filter.
 *
 * Returns the input unchanged when it cannot be trusted, so contract drift shows the
 * server's own value rather than crashing the row or inventing a date.
 */
export function formatSubmittedDate(isoTimestamp: string): string {
  const parsed = trustedInstant(isoTimestamp);
  if (parsed === null) return isoTimestamp;
  // Local-zone fields, assembled by hand rather than through `toLocaleString`, so the
  // output is DD/MM/YYYY on every machine. An `Intl` locale would hand a US-configured
  // browser 02/21/2026.
  const day = pad(parsed.getDate());
  const month = pad(parsed.getMonth() + 1);
  const year = parsed.getFullYear();
  return `${day}/${month}/${year}`;
}

/**
 * The parsed instant, or null when the value cannot be trusted: unparseable, or a day out
 * of range. `new Date` does NOT reject an out-of-range day: it rolls it over, so
 * `2026-02-30T00:00:00.000Z` parses happily and comes back as 2026-03-02. The NaN check
 * never sees it (an out-of-range MONTH or HOUR does yield NaN, only the day overflows
 * silently). A value that round-trips to a different UTC calendar date than the one it
 * stated is drift, so callers return it unchanged rather than render a date nobody sent.
 * Checked against the UTC date because that is the half the wire value states; the
 * DISPLAY is local and may legitimately differ.
 */
function trustedInstant(isoTimestamp: string): Date | null {
  const parsed = new Date(isoTimestamp);
  if (Number.isNaN(parsed.getTime())) return null;
  if (
    isUtcDateStated(isoTimestamp) &&
    !isoTimestamp.startsWith(parsed.toISOString().slice(0, 10))
  ) {
    return null;
  }
  return parsed;
}

// Story 8.10 (ADO-2019), DV-38: the HHT dates, in the viewer's zone as the order history
// above (no `timeZone`), Irish English, a three-letter month and the app's 12-hour
// "7:42am" (the cut-off display's style; cut-offs themselves stay in Ireland time,
// `cutOffDisplay.ts`). Read from the parts so the separators do not drift with the ICU
// version.
const LOCAL_DATE_TIME_PARTS = new Intl.DateTimeFormat('en-IE', {
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
});

interface LocalDateTimeParts {
  readonly weekday: string;
  readonly day: string;
  readonly month: string;
  readonly year: string;
  /** "7:42am". */
  readonly time: string;
}

/** The local parts of a trusted instant, or null (the caller returns its input). */
function localDateTimeParts(isoTimestamp: string): LocalDateTimeParts | null {
  const parsed = trustedInstant(isoTimestamp);
  if (parsed === null) return null;
  const parts = LOCAL_DATE_TIME_PARTS.formatToParts(parsed);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((candidate) => candidate.type === type)?.value ?? '';
  const dayPeriod = part('dayPeriod')
    .toLowerCase()
    .replace(/[^a-z]/g, '');
  return {
    weekday: part('weekday'),
    day: part('day'),
    // en-IE abbreviates September as "Sept"; the design uses three letters.
    month: part('month').slice(0, 3),
    year: part('year'),
    time: `${part('hour')}:${part('minute')}${dayPeriod}`,
  };
}

/** "Wed 23 Sep 2026, 7:42am" (the HHT waiting orders list). Untrusted input is returned
 *  unchanged. */
export function formatWeekdayDateTimeWithYear(isoTimestamp: string): string {
  const parts = localDateTimeParts(isoTimestamp);
  if (parts === null) return isoTimestamp;
  return `${parts.weekday} ${parts.day} ${parts.month} ${parts.year}, ${parts.time}`;
}

/** "Wed 23 Sep, 7:42am" (the HHT review's "Scanned"). Untrusted input is returned
 *  unchanged. */
export function formatWeekdayDateTime(isoTimestamp: string): string {
  const parts = localDateTimeParts(isoTimestamp);
  if (parts === null) return isoTimestamp;
  return `${parts.weekday} ${parts.day} ${parts.month}, ${parts.time}`;
}

/** "10 Sep 2026, 2:18pm" (the HHT history). Untrusted input is returned unchanged. */
export function formatDateTimeWithYear(isoTimestamp: string): string {
  const parts = localDateTimeParts(isoTimestamp);
  if (parts === null) return isoTimestamp;
  return `${parts.day} ${parts.month} ${parts.year}, ${parts.time}`;
}

/**
 * Q4 ("Already added"): "2:18pm" when the instant falls on `nowMs`'s local day, else "2:18pm on
 * 10 Sep 2026", in the viewer's zone (DV-38). Untrusted input is returned unchanged.
 */
export function formatTimeWithDateUnlessToday(isoTimestamp: string, nowMs: number): string {
  const parts = localDateTimeParts(isoTimestamp);
  if (parts === null) return isoTimestamp;
  const today = localDateTimeParts(new Date(nowMs).toISOString());
  const sameDay =
    today !== null &&
    today.day === parts.day &&
    today.month === parts.month &&
    today.year === parts.year;
  return sameDay ? parts.time : `${parts.time} on ${parts.day} ${parts.month} ${parts.year}`;
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

/**
 * Whether the wire value states a UTC calendar date the parse can be compared against:
 * `YYYY-MM-DD`, either bare or leading a `Z`-suffixed timestamp. A value carrying an
 * offset such as `+01:00` is excluded, because its UTC date is legitimately allowed to
 * differ from the local one it states.
 */
function isUtcDateStated(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}(T[\d:.]+Z)?$/.test(value);
}
